import React, { type FunctionComponent, type ChangeEvent, useState, useMemo, useEffect, useCallback } from 'react';
import { AddIcon } from '@chakra-ui/icons';
import type { AxiosError } from 'axios';
import { ContentContainer } from '../../components/ContentContainer';
import { type Column } from 'react-table';
import { TableContainer } from '../../components/table/TableContainer';
import type { Project, IProject, IProjects, Language, Translation } from '../../interface/Localization';
import { Button, Flex, Input, Progress, useToast } from '@chakra-ui/react';
import { Request as apiRequest } from '../../helper';
import { useNavigate } from 'react-router';
import { LocalizationsTableComponent } from './LocalizationsTableComponent';
import { CreateTranslationModal } from './CreateTranslationModal';

interface CreateLocalizationKeyPayload {
    name: string;
    values?: Array<{
        language: string;
        value: string;
    }>;
}

const buildCreateLocalizationKeyPayload = (name: string, drafts: Translation[]): CreateLocalizationKeyPayload => {
    const valuesByLanguage = new Map<string, string>();

    drafts.forEach((draft) => {
        const language = draft.language?.trim();
        const value = draft.value ?? '';

        if (!language || !value.trim() || valuesByLanguage.has(language)) {
            return;
        }

        valuesByLanguage.set(language, value);
    });

    const values = Array.from(valuesByLanguage.entries()).map(([language, value]) => ({
        language,
        value,
    }));

    return {
        name: name.trim(),
        ...(values.length > 0 ? { values } : {}),
    };
};

const toastCreateKeyError = (toast: ReturnType<typeof useToast>, error: unknown): void => {
    const status = (error as AxiosError)?.response?.status;

    if (status === 404) {
        toast({
            title: 'Project not found.',
            status: 'error',
            duration: 3000,
            isClosable: true,
        });
        return;
    }

    if (status === 409) {
        toast({
            title: 'Translation key already exists.',
            status: 'error',
            duration: 3000,
            isClosable: true,
        });
        return;
    }

    if (status === 400) {
        toast({
            title: 'Invalid key payload or unknown language.',
            status: 'error',
            duration: 3000,
            isClosable: true,
        });
        return;
    }

    toast({
        title: 'Error creating translation.',
        status: 'error',
        duration: 3000,
        isClosable: true,
    });
};

export const LocalizationsDashboardList: FunctionComponent = () => {
    const [projects, setProjects] = useState<Project[]>();
    const [data, setData] = useState<Record<string, Translation[]>>();
    const [filteredData, setFilteredData] = useState<Record<string, Translation[]>>();
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [isCreateTranslationDialogOpen, setIsCreateTranslationDialogOpen] = useState<boolean>(false);
    const [newTranslationDrafts, setNewTranslationDrafts] = useState<Translation[]>([]);
    const [newTranslationKey, setNewTranslationKey] = useState<string>('');
    const [selectedProjectId, setSelectedProjectId] = useState<string>('');
    const [isSavingNewTranslation, setIsSavingNewTranslation] = useState<boolean>(false);
    const navigate = useNavigate();
    const toast = useToast();

    const filterTranslations = useCallback(
        (translations: Record<string, Translation[]>, queryString: string): Record<string, Translation[]> => {
            const filteredEntries: any = [];
            queryString = queryString.toLowerCase();
            Object.entries(translations).forEach((translation) => {
                if (
                    translation[1].some(
                        (trans) =>
                            (trans.value?.toLowerCase().includes(queryString) ?? false) ||
                            trans.key.toLowerCase().includes(queryString),
                    )
                ) {
                    filteredEntries.push(translation);
                }
            });

            return Object.fromEntries(filteredEntries);
        },
        [],
    );

    const reloadProjectsAndTranslations = useCallback(
        async (queryString = ''): Promise<void> => {
            const response = await apiRequest.get<IProjects>({ path: '/localization/project' });
            const projects = response.data.result;
            projects.forEach((project) => {
                project.state = project.percent_done === 100 ? 2 : project.percent_done > 0 ? 1 : 0;
            });
            setProjects(response.data.result);

            const translations: Translation[] = [];
            projects.forEach((project) => {
                const languages = project.language;
                languages?.forEach((language) => {
                    language.translation?.forEach((trans) => {
                        trans.placeholder = trans.value;
                        trans.state = trans.value?.trim() ? 2 : 1;
                        trans.language = language.id;
                        trans.project = project.name;
                        trans.project_id = project.id;
                        translations.push(trans);
                    });
                });
            });

            const groupedItems = translations.reduce<Record<string, Translation[]>>((acc, translation) => {
                if (!acc[translation.id]) {
                    acc[translation.id] = [];
                }
                acc[translation.id].push(translation);
                return acc;
            }, {});
            setData(groupedItems);
            if (queryString) {
                const filteredTranslations = filterTranslations(groupedItems, queryString);
                setFilteredData(filteredTranslations);
            } else {
                setFilteredData(groupedItems);
            }
        },
        [filterTranslations],
    );

    useEffect(() => {
        setProjects(undefined);
        void reloadProjectsAndTranslations(searchQuery);
    }, [reloadProjectsAndTranslations]);

    const filterData = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const query = event.target.value;
            setSearchQuery(query);
            if (query) {
                if (data) {
                    const filteredResults = filterTranslations(data, query);
                    setFilteredData(filteredResults);
                }
            } else {
                setFilteredData(data);
            }
        },
        [data, filterTranslations],
    );

    const projectOptions = useMemo(
        () => (projects ?? []).map((project) => ({ id: project.id, name: project.name })),
        [projects],
    );

    const translationTemplateByProject = useMemo(() => {
        const templates = new Map<string, Translation[]>();

        Object.values(data ?? {}).forEach((group) => {
            const firstTranslation = group[0];
            const derivedProjectId = firstTranslation?.project_id?.trim();

            if (!firstTranslation || !derivedProjectId || templates.has(derivedProjectId)) {
                return;
            }

            templates.set(derivedProjectId, group);
        });

        return templates;
    }, [data]);

    useEffect(() => {
        if (projectOptions.length === 0) {
            setSelectedProjectId('');
            return;
        }

        if (selectedProjectId && projectOptions.some((project) => project.id === selectedProjectId)) {
            return;
        }

        const defaultProjectId =
            projectOptions.find((project) => project.name.trim().toLowerCase() === 'kernel')?.id ??
            projectOptions[0].id;
        setSelectedProjectId(defaultProjectId);
    }, [projectOptions, selectedProjectId]);

    const getDraftsForProject = (targetProjectId: string): Translation[] => {
        const translationTemplate = translationTemplateByProject.get(targetProjectId);
        const projectName = projectOptions.find((project) => project.id === targetProjectId)?.name;

        if (!translationTemplate) {
            return [];
        }

        return translationTemplate.map((translation) => ({
            ...translation,
            id: '',
            value_id: '',
            key: '',
            value: '',
            placeholder: '',
            project_id: targetProjectId,
            project: projectName,
        }));
    };

    const openCreateTranslationDialog = (): void => {
        if (!selectedProjectId) {
            return;
        }

        setNewTranslationKey('');
        setNewTranslationDrafts(getDraftsForProject(selectedProjectId));
        setIsCreateTranslationDialogOpen(true);
    };

    const closeCreateTranslationDialog = (): void => {
        setIsCreateTranslationDialogOpen(false);
        setNewTranslationDrafts([]);
        setNewTranslationKey('');
    };

    const handleNewTranslationValueChange = (language: string, value: string): void => {
        setNewTranslationDrafts((prevData) =>
            prevData.map((translation) =>
                translation.language === language ? { ...translation, value, placeholder: value } : translation,
            ),
        );
    };

    const handleNewTranslationProjectChange = (nextProjectId: string): void => {
        setSelectedProjectId(nextProjectId);
        setNewTranslationDrafts(getDraftsForProject(nextProjectId));
    };

    const saveNewTranslations = async (): Promise<void> => {
        if (newTranslationDrafts.length === 0 || !newTranslationKey.trim() || !selectedProjectId.trim()) {
            return;
        }

        setIsSavingNewTranslation(true);

        try {
            await apiRequest.post<IProject>({
                path: '/localization/project/' + selectedProjectId + '/key',
                data: buildCreateLocalizationKeyPayload(newTranslationKey, newTranslationDrafts),
            });

            toast({
                title: 'Translation created.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            closeCreateTranslationDialog();
            await reloadProjectsAndTranslations(searchQuery);
        } catch (error) {
            toastCreateKeyError(toast, error);
        } finally {
            setIsSavingNewTranslation(false);
        }
    };

    const openDetails = (selectedProject: Project): void => {
        navigate('/localizations/' + selectedProject.id);
    };

    const tableData = useMemo(() => projects, [projects]);
    const columns: Array<Column<Project>> = useMemo(
        () => [
            {
                Header: 'state',
                accessor: 'state',
            },
            {
                Header: 'ID',
                accessor: 'id',
            },
            {
                Header: 'Name',
                accessor: 'name',
            },
            {
                Header: 'Description',
                accessor: 'description',
            },
            {
                Header: 'Created At',
                accessor: 'created_at',
            },
            {
                Header: 'Updated At',
                accessor: 'updated_at',
            },
            {
                Header: 'value',
                accessor: 'language',
                Cell: (cell: any) => {
                    const language: Language[] = cell.row.original.language;
                    const displayLanguage = [...(language ?? [])]
                        .sort((a, b) => (a.code ?? '').localeCompare(b.code ?? ''))
                        .map((element) => element.code.toUpperCase())
                        .join(', ');

                    return <p>{displayLanguage}</p>;
                },
            },
            {
                Header: 'Progress',
                accessor: 'percent_done',
                Cell: (cell: any) => {
                    return <Progress value={cell.row.original.percent_done}></Progress>;
                },
            },
        ],
        [projects],
    );

    return (
        <ContentContainer headline="Localizations" description="List of all Projects" showSpinner={!projects}>
            <Flex
                align={{ base: 'stretch', md: 'center' }}
                justify="space-between"
                direction={{ base: 'column', md: 'row' }}
                gap={4}
            >
                <Input
                    placeholder="Search for localization"
                    size="md"
                    type="search"
                    onChange={filterData}
                    value={searchQuery}
                    flex="1"
                    minW={{ base: '100%', md: '0' }}
                />
                <Button
                    colorScheme="blue"
                    leftIcon={<AddIcon />}
                    onClick={openCreateTranslationDialog}
                    flexShrink={0}
                    whiteSpace="nowrap"
                >
                    Create new translation
                </Button>
            </Flex>
            {!searchQuery && tableData ? (
                <TableContainer
                    columns={columns}
                    data={tableData}
                    rowClick={openDetails}
                    hiddenColumns={['state', 'id', 'created_at', 'updated_at', 'description']}
                    showColoredState={true}
                    coloredStateMethod={colorForState}
                    initialState={{
                        sortBy: [{ id: 'order', desc: true }],
                    }}
                    defaultPageSize={20}
                />
            ) : (
                filteredData &&
                searchQuery && (
                    <LocalizationsTableComponent
                        translationsData={filteredData}
                        searchQuery={true}
                        onDataRefresh={async () => {
                            await reloadProjectsAndTranslations(searchQuery);
                        }}
                    />
                )
            )}
            <CreateTranslationModal
                isOpen={isCreateTranslationDialogOpen}
                onClose={closeCreateTranslationDialog}
                projectOptions={projectOptions}
                selectedProjectId={selectedProjectId}
                onProjectChange={handleNewTranslationProjectChange}
                newTranslationKey={newTranslationKey}
                onKeyChange={setNewTranslationKey}
                newTranslationDrafts={newTranslationDrafts}
                onValueChange={handleNewTranslationValueChange}
                onSave={saveNewTranslations}
                isSaving={isSavingNewTranslation}
            />
        </ContentContainer>
    );
};

enum StateType {
    NotStarted = 0,
    InProgress = 1,
    Done = 2,
}

const colorForState = (state: StateType): string => {
    switch (state) {
        case StateType.Done:
            return 'green.500';
        case StateType.InProgress:
            return 'blue.500';
        case StateType.NotStarted:
            return 'red.500';
    }
};
